<template>
  <aside
    class="customer-sidebar"
    :class="{ 'customer-sidebar--collapsed': isCollapsed }"
  >
    <nav class="customer-sidebar__nav">
      <template v-for="(item, idx) in topLevelNav" :key="item.menuItemId || item.catalogKey">
          
          <button
            v-if="item.itemKind === 'group'"
            type="button"
            class="nav-item nav-item--parent"
            :class="{ active: isGroupActive(item.catalogKey) }"
            v-tooltip.right="{ value: navLabel(item), class: 'customer-sidebar-tooltip' }"
            @click="toggleGroup(item.catalogKey)"
            @touchend="autoHideTooltip"
          >
            <div class="nav-item__icon">
              <i :class="iconForNavItem(item)"></i>
            </div>
            <span v-show="!isCollapsed" class="nav-item__label">{{ navLabel(item) }}</span>
            <i
              v-show="!isCollapsed"
              class="pi nav-item__chevron"
              :class="isGroupExpanded(item.catalogKey) ? 'pi-chevron-down' : 'pi-chevron-right'"
            />
          </button>
          <div
            v-if="item.itemKind === 'group' && isGroupExpanded(item.catalogKey) && !isCollapsed"
            class="nav-sub-items"
          >
            <template v-for="child in childrenFor(item.catalogKey)" :key="child.menuItemId || child.catalogKey">
              <router-link
                v-if="!child.locked && !isOverlayLink(child)"
                :to="child.route"
                class="nav-item nav-item--sub"
                :class="{ active: isActive(child.route) }"
                v-tooltip.right="{ value: navLabel(child), class: 'customer-sidebar-tooltip' }"
                @touchend="autoHideTooltip"
              >
                <div class="nav-item__icon">
                  <i :class="iconForNavItem(child)"></i>
                </div>
                <span class="nav-item__label">{{ navLabel(child) }}</span>
              </router-link>
              <button
                v-else-if="!child.locked && isOverlayLink(child)"
                type="button"
                class="nav-item nav-item--sub"
                v-tooltip.right="{ value: navLabel(child), class: 'customer-sidebar-tooltip' }"
                @click="onNavClick(child)"
                @touchend="autoHideTooltip"
              >
                <div class="nav-item__icon">
                  <i :class="iconForNavItem(child)"></i>
                </div>
                <span class="nav-item__label">{{ navLabel(child) }}</span>
              </button>
              <button
                v-else
                type="button"
                class="nav-item nav-item--sub nav-item--locked"
                v-tooltip.right="{ value: navLabel(child), class: 'customer-sidebar-tooltip' }"
                @click="onNavClick(child)"
                @touchend="autoHideTooltip"
              >
                <div class="nav-item__icon">
                  <i class="pi pi-lock"></i>
                </div>
                <span class="nav-item__label">{{ navLabel(child) }}</span>
              </button>
            </template>
          </div>
          <router-link
            v-else-if="item.itemKind === 'link' && !item.locked && !isOverlayLink(item)"
            :to="item.route"
            class="nav-item"
            :class="{ active: item.route === '/' ? isExactActive('/') : isActive(item.route) }"
            v-tooltip.right="{ value: navLabel(item), class: 'customer-sidebar-tooltip' }"
            @touchend="autoHideTooltip"
          >
            <div class="nav-item__icon">
              <i :class="iconForNavItem(item)"></i>
            </div>
            <span v-show="!isCollapsed" class="nav-item__label">{{ navLabel(item) }}</span>
          </router-link>
          <button
            v-else-if="item.itemKind === 'link' && !item.locked && isOverlayLink(item)"
            type="button"
            class="nav-item"
            v-tooltip.right="{ value: navLabel(item), class: 'customer-sidebar-tooltip' }"
            @click="onNavClick(item)"
            @touchend="autoHideTooltip"
          >
            <div class="nav-item__icon">
              <i :class="iconForNavItem(item)"></i>
            </div>
            <span v-show="!isCollapsed" class="nav-item__label">{{ navLabel(item) }}</span>
          </button>
          <button
            v-else-if="item.itemKind === 'link' && item.locked"
            type="button"
            class="nav-item nav-item--locked"
            v-tooltip.right="{ value: navLabel(item), class: 'customer-sidebar-tooltip' }"
            @click="onNavClick(item)"
            @touchend="autoHideTooltip"
          >
            <div class="nav-item__icon">
              <i class="pi pi-lock"></i>
            </div>
            <span v-show="!isCollapsed" class="nav-item__label">{{ navLabel(item) }}</span>
          </button>
        </template>
        <template v-if="showAdminCmLink">
          <div class="nav-divider" />
          <router-link
            to="/contentManagement"
            class="nav-item"
            :class="{ active: isActive('/contentManagement') }"
            v-tooltip.right="{ value: 'Content Management', class: 'customer-sidebar-tooltip' }"
            @touchend="autoHideTooltip"
          >
            <div class="nav-item__icon">
              <i class="pi pi-file-edit"></i>
            </div>
            <span v-show="!isCollapsed" class="nav-item__label">Content Management</span>
          </router-link>
        </template>
    </nav>

    <!-- Footer: Create Activity (conditional) + Toggle -->
    <div class="customer-sidebar__footer">
      <button
        type="button"
        class="create-activity-btn"
        data-testid="customer-create-activity"
        @click="openCreateOverlay"
        v-tooltip.right="{ value: $t('HeaderComponent.create'), class: 'customer-sidebar-tooltip' }"
        @touchend="autoHideTooltip"
      >
        <i class="pi pi-plus"></i>
        <span v-show="!isCollapsed">{{ $t('HeaderComponent.create') }}</span>
      </button>
      <button
        type="button"
        class="sidebar-toggle"
        v-tooltip.right="{ value: isCollapsed ? $t('CustomerSidebar.expand') : $t('CustomerSidebar.collapse'), class: 'customer-sidebar-tooltip' }"
        @click="layoutStore.toggleSidebar()"
        @touchend="autoHideTooltip"
      >
        <i :class="isCollapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-left'"></i>
      </button>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { navigateCustomerNavItem } from '@/utils/customerNavClick';
import { useLayoutStore } from '@/stores/layout';
import {
  buildNavigationTree,
  iconForNavItem,
  labelForNavItem,
} from '@/utils/customerNavigation';
import { userIsTenantAdmin } from '@/utils/userRoles';
import { resolveCustomerAppLocale } from '@/utils/featureDisplay';

const toast = useToast();
const { t, locale } = useI18n();

const ACTIVITY_ROUTES = [
  '/travel-buddy-history',
  '/personal-goals',
  '/personal-journey',
  '/reflexionGuideline',
  '/external-evaluation',
  '/kss-tracker',
  '/workshops',
];

const route = useRoute();
const router = useRouter();
const store = useStore();
const layoutStore = useLayoutStore();

const groupExpanded = ref({ activities: true, administration: false });

const isCollapsed = computed(() => layoutStore.isSidebarCollapsed);

watch(
  () => route.path,
  () => {
    if (isCollapsed.value) return;
    if (route.path === '/' || ACTIVITY_ROUTES.some((path) => route.path.startsWith(path))) {
      groupExpanded.value.activities = true;
    }
    if (['/user-administration', '/team-administration'].some((path) => route.path.startsWith(path))) {
      groupExpanded.value.administration = true;
    }
  },
);

const currentUser = computed(() => store.getters.getUser || {});

const navLocale = computed(() => resolveCustomerAppLocale(store, locale.value));
const navTree = computed(() => buildNavigationTree(store.getters.getCustomerNavigation || []));
const topLevelNav = computed(() => navTree.value.topLevel);
const childrenFor = (parentKey) => navTree.value.childrenByParent[parentKey] || [];

const showAdminCmLink = computed(() => userIsTenantAdmin(currentUser.value));

function navLabel(item) {
  return labelForNavItem(item, navLocale.value);
}

function isGroupExpanded(catalogKey) {
  return groupExpanded.value[catalogKey] !== false;
}

function toggleGroup(catalogKey) {
  if (isCollapsed.value) {
    layoutStore.setSidebarCollapsed(false);
    groupExpanded.value[catalogKey] = true;
  } else {
    groupExpanded.value[catalogKey] = !isGroupExpanded(catalogKey);
  }
}

function isGroupActive(catalogKey) {
  const children = childrenFor(catalogKey);
  return children.some((c) => c.route && route.path.startsWith(c.route));
}

function autoHideTooltip() {
  setTimeout(() => {
    document.querySelectorAll('.p-tooltip').forEach(el => el.remove());
  }, 500);
}

function isActive(path) {
  return route.path.startsWith(path);
}
function isExactActive(path) {
  if (path === '/') return route.path === '/' || route.path === '';
  return route.path === path;
}

function openCreateOverlay() {
  store.commit('addFrontendOverlay', { id: Math.random(), type: 'taskCreator' });
}

function isOverlayLink(item) {
  return item?.openAs === 'overlay' || (item?.itemKind === 'link' && !item?.route);
}

function onNavClick(item) {
  const handled = navigateCustomerNavItem(router, store, item);
  if (handled === false) {
    toast.add({
      severity: 'warn',
      summary: t('CustomerSidebar.overlayUnavailableTitle'),
      detail: t('CustomerSidebar.overlayUnavailableDetail'),
      life: 4000,
    });
  }
}
</script>

<style scoped>
.customer-sidebar {
  --sidebar-bg: #ffffff;
  --sidebar-border: #e7e7e7;
  --nav-text: #666666;
  --nav-text-active: #1E2019;
  --nav-hover-bg: rgba(228, 130, 26, 0.08);

  width: 260px;
  min-width: 260px;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  font-family: 'AsenPro', sans-serif;
  transition: width 0.25s ease, min-width 0.25s ease;
  flex-shrink: 0;
}

.customer-sidebar--collapsed {
  width: 72px;
  min-width: 72px;
}

.customer-sidebar__nav {
  flex: 1;
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-radius: 0.75rem;
  color: var(--nav-text);
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
  border: 1px solid transparent;
  background: transparent;
  font: inherit;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.nav-item:hover {
  background: var(--nav-hover-bg);
  color: #e4821a;
}

.nav-item.active {
  background: rgba(228, 130, 26, 0.1);
  color: var(--nav-text-active);
  border-color: rgba(228, 130, 26, 0.2);
}

.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  background: linear-gradient(180deg, #c83a77, #e44b55, #e4821a);
  border-radius: 0 3px 3px 0;
}

.nav-item__icon {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: #e4821a;
}

.nav-item__icon--accent {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: white;
}

.nav-item.active .nav-item__icon {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: white;
}

.nav-item__label {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-divider {
  height: 1px;
  background: var(--sidebar-border);
  margin: 0.5rem 0.75rem;
}

.nav-item--parent {
  justify-content: flex-start;
}

.nav-item__chevron {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--nav-text);
}

.nav-sub-items {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding-left: 0.5rem;
  margin-left: 2.25rem;
  border-left: 1px solid var(--sidebar-border);
}

.nav-item--sub .nav-item__icon {
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  font-size: 0.875rem;
}

.customer-sidebar__footer {
  padding: 0.75rem;
  border-top: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.create-activity-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--accent-gradient);
  border: none;
  border-radius: 2rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.create-activity-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(228, 130, 26, 0.3);
}

.sidebar-toggle {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border: 1px solid rgba(228, 130, 26, 0.2);
  border-radius: 0.5rem;
  color: #e4821a;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.sidebar-toggle:hover {
  background: rgba(228, 130, 26, 0.15);
}

</style>

<style>
/* Unscoped: v-tooltip content is portaled; size to content, no word break */
.customer-sidebar-tooltip {
  width: max-content !important;
  max-width: none !important;
  white-space: nowrap !important;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}
</style>
